import { Button } from '@mui/material';
import React, { FC, PropsWithChildren } from 'react';

import useIsMobile from '../../../../hooks/useIsMobile';
import translate from '../../../../i18n/translate';

import LabelCreatePopover from '../../LabelCreatePopover';
import MuiIcon from '../../MuiIcon';
import { ExternalPopoverProps } from '../../Popover';

import LabelCreateModal from '../LabelCreateModal';

import * as Styled from './styled';

type Props = PropsWithChildren & {
  externalHandlerProps: ExternalPopoverProps;
  formId?: string;
  isActive?: boolean;
  onClose?: () => void;
  isClearDisabled: boolean;
  requestClear: () => void;
};

/**
 * Label create component
 *
 * @todo Probably remove this component and use MetaContainerPopup when it stars using the new popover
 *
 * @param props                      Props passed to the component
 * @param props.children             The children to render inside the popover
 * @param props.externalHandlerProps Props from the external handler
 * @param props.formId               The ID of the form
 * @param props.isActive             Whether the component should be visible
 * @param props.onClose              Callback to close the modal
 * @param props.isClearDisabled      Whether the clear button should be disabled
 * @param props.requestClear         Callback to clear the form
 * @returns                          The component itself
 */
const LabelCreate: FC<Props> = ({
  children,
  externalHandlerProps,
  formId,
  onClose,
  isClearDisabled,
  requestClear,
  isActive = false,
}) => {
  const isMobile = useIsMobile();

  const content = (
    <>
      <Styled.Content>{children}</Styled.Content>
      <Styled.Footer>
        <Button color="error" disabled={isClearDisabled} onClick={requestClear}>
          {translate('GENERAL__CLEAR')}
        </Button>
        <Button form={formId} type="submit">
          {translate('GENERAL__SAVE')}
        </Button>
      </Styled.Footer>
    </>
  );

  if (isMobile) {
    return (
      <LabelCreateModal
        isActive={isActive}
        onClose={onClose}
        requestClear={requestClear}
      >
        {content}
      </LabelCreateModal>
    );
  }

  return (
    <LabelCreatePopover
      externalHandlerProps={externalHandlerProps}
      requestClear={requestClear}
      trigger={
        <Styled.CreateLabelButton startIcon={<MuiIcon icon="add" />}>
          {translate('COMPOSE__LABELS__CREATE')}
        </Styled.CreateLabelButton>
      }
    >
      {content}
    </LabelCreatePopover>
  );
};

export default LabelCreate;
